import { Sparkles, Heart } from "lucide-react";
import { MeasuringTapeLoader } from "@/components/find-my-size/MeasuringTapeLoader";
import { HostStockBadge } from "@/components/find-my-size/HostStockBadge";
import { BODY_META_LABELS, buildProfileChips } from "../constants";
import { InfoNote } from "../parts/InfoNote";
import { ProfileChipList } from "../parts/ProfileChipList";
import { BodyStep } from "./BodyStep";

// ═══════════════════════════════════════════════════════════════════════════
// STEP: Karşılama — Smart Recall (3 varyant + always_confirm)
//
// mode = "empty"     → İlk ziyaretçi. Merchant paneline özel açıklama gösterilir.
//                      Live'da karşılama tamamen atlanır, direkt basic'ten başlar.
// mode = "partial"   → Kısmi profil. Bilinen chip'ler + eksik alan için inline
//                      mini-question (BodyStep). Auto-advance ile profil dolunca
//                      complete moduna geçer.
// mode = "recommend" → Tam profil + auto_recommend. Direkt öneri (dev typography
//                      beden) + tüm chip'ler + "Bu Bedenle Devam Et" CTA.
// mode = "confirm"   → Tam profil + always_confirm. "Bilgilerin doğru mu?" onayı
//                      önce görünür, onaylanınca result'a atlar.
// ═══════════════════════════════════════════════════════════════════════════
export const WelcomeStep = ({ profile, copy, resultCopy, accent, mode, analysis, recommendation, outOfRangeStrategy, hostStockStatus, highlightedKey, onMissingBodySelect }) => {
    // ─── STATE 1: EMPTY ────────────────────────────────────────────────────
    if (mode === "empty") {
        return (
            <div className="py-10 text-center space-y-3" data-testid="fms-step-welcome-empty">
                <div className="mx-auto sayz-fms-reveal" style={{ animationDelay: "0ms" }}>
                    <span
                        className="inline-flex h-12 w-12 items-center justify-center rounded-full"
                        style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)`, color: accent }}
                    >
                        <Sparkles className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                </div>
                <div className="sayz-fms-reveal" style={{ animationDelay: "80ms" }}>
                    <h4 className="text-[18px] font-semibold text-foreground tracking-tight">
                        İlk Ziyaret · Karşılama Atlanır
                    </h4>
                    <p className="text-[13px] text-muted-foreground leading-relaxed max-w-[340px] mx-auto mt-2">
                        Bu ziyaretçi wizard&apos;ı ilk kez açtığı için karşılama ekranı gösterilmez — direkt ilk soruya (boy/kilo/yaş) götürülür.
                    </p>
                </div>
            </div>
        );
    }

    const chips = buildProfileChips(profile);

    // ─── STATE 2: PARTIAL ──────────────────────────────────────────────────
    if (mode === "partial") {
        const missingField = analysis.missingBody[0] || analysis.missingBasic[0];
        const isBody = analysis.missingBody.includes(missingField);
        return (
            <div className="space-y-6" data-testid="fms-step-welcome-partial">
                <div>
                    <div className="flex items-center gap-2 sayz-fms-reveal" style={{ animationDelay: "0ms" }}>
                        <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full"
                            style={{ background: accent, color: "#fff" }}
                        >
                            <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                        </span>
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
                            {copy.welcomePartialLabel || "Neredeyse Hazırız"}
                        </span>
                    </div>
                    <h3 className="text-[24px] font-bold text-foreground tracking-tight mt-3 leading-tight sayz-fms-reveal" style={{ animationDelay: "80ms" }}>
                        {copy.welcomePartialTitle || "Bir şey daha soracağım"}
                    </h3>
                    <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed sayz-fms-reveal" style={{ animationDelay: "140ms" }}>
                        {copy.welcomePartialDesc || `Bu ürün için ${BODY_META_LABELS[missingField] || "bir ölçüyü"} da bilmem gerekiyor. Sonra hemen bedenini bulacağım.`}
                    </p>
                </div>

                {/* Bilinenler — chip özeti */}
                <ProfileChipList chips={chips} highlightedKey={highlightedKey} />

                {/* Eksik alan — inline mini-question (body step) */}
                {isBody && (
                    <div className="pt-1 sayz-fms-reveal" style={{ animationDelay: "260ms" }}>
                        <BodyStep
                            kind={missingField}
                            profile={profile}
                            onSelect={(v) => onMissingBodySelect(missingField, v)}
                            copy={copy}
                            accent={accent}
                        />
                    </div>
                )}
            </div>
        );
    }

    // ─── STATE 3: CONFIRM (always_confirm mode) ───────────────────────────
    if (mode === "confirm") {
        return (
            <div className="space-y-6" data-testid="fms-step-welcome-confirm">
                <div>
                    <div className="flex items-center gap-2 sayz-fms-reveal" style={{ animationDelay: "0ms" }}>
                        <span
                            className="inline-flex h-7 w-7 items-center justify-center rounded-full"
                            style={{ background: accent, color: "#fff" }}
                        >
                            <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                        </span>
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
                            {copy.welcomeConfirmLabel || copy.welcomeLabel || "Merhaba Tekrar"}
                        </span>
                    </div>
                    <h3 className="text-[24px] font-bold text-foreground tracking-tight mt-3 leading-tight sayz-fms-reveal" style={{ animationDelay: "80ms" }}>
                        {copy.welcomeConfirmTitle || "Bilgilerin hâlâ doğru mu?"}
                    </h3>
                    <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed sayz-fms-reveal" style={{ animationDelay: "140ms" }}>
                        {copy.welcomeConfirmDesc || "Bu bilgileri en son verdiğinden bu yana bir değişiklik yoksa, direkt önerimi göstereyim."}
                    </p>
                </div>
                <ProfileChipList chips={chips} />
            </div>
        );
    }

    // ─── STATE 4: RECOMMEND (auto_recommend + complete) ────────────────────
    // While the backend computes the recommendation we show the exact same
    // Measuring Tape ritual the Result step uses — same component, same
    // minimum ritual window, same CLS envelope. This keeps the branded
    // micro-moment consistent whether the visitor lands on Welcome
    // Recommend or on Result after a wizard walk-through.
    if (!recommendation) {
        return (
            <MeasuringTapeLoader
                profile={profile}
                accent={accent}
                copy={{
                    // Reuse the welcome header language ("MERHABA TEKRAR")
                    // so the loader slot reads as continuity with the
                    // Welcome Recommend state it will swap into — the
                    // visitor sees a single composed moment, not two
                    // disconnected screens.
                    resultLabel: copy.welcomeLabel || "Merhaba Tekrar",
                    resultLoadingText: copy.welcomeLoadingText || "Bedenin hesaplanıyor",
                }}
            />
        );
    }

    // Nazik Ret parity with the Result step. If the merchant chose
    // `kind_refusal` as their out-of-range strategy, the Welcome Recommend
    // fast-path has to honour that too — otherwise the visitor sees "Seni
    // hatırlıyorum, Önerim: XL" on the welcome screen and gets Nazik Ret on
    // Result for the exact same profile, which reads as a bug. Showing
    // refusal here keeps the merchant's "don't ship a shaky guess" policy
    // consistent across every recommendation surface in the widget.
    if (recommendation.outOfRange && outOfRangeStrategy === "kind_refusal") {
        return (
            <div className="flex flex-col items-center justify-center text-center py-4 space-y-4 min-h-full" data-testid="fms-step-welcome-recommend-refusal">
                <span
                    className="inline-flex h-12 w-12 items-center justify-center rounded-full sayz-fms-reveal"
                    style={{ background: accent, color: "#fff", animationDelay: "0ms" }}
                >
                    <Heart className="h-5 w-5" strokeWidth={1.75} fill="currentColor" />
                </span>
                <span
                    className="text-[11px] font-semibold uppercase tracking-wider sayz-fms-reveal"
                    style={{ color: accent, animationDelay: "80ms" }}
                >
                    {resultCopy.refusalLabel || "Emin değiliz"}
                </span>
                <p
                    className="text-[15px] text-foreground leading-relaxed max-w-[360px] sayz-fms-reveal"
                    style={{ animationDelay: "160ms" }}
                >
                    {resultCopy.refusalText || "Bu ürün için sana ideal bedeni tam olarak bulamadık. Yanlış bedenle iade yaşamamak için beden tablosunu incelemeni öneririm."}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center text-center space-y-5 min-h-full" data-testid="fms-step-welcome-recommend">
            {/* İkon */}
            <span
                className="inline-flex h-11 w-11 items-center justify-center rounded-full sayz-fms-reveal"
                style={{ background: accent, color: "#fff", animationDelay: "0ms" }}
            >
                <Sparkles className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </span>

            {/* MERHABA TEKRAR label */}
            <span
                className="text-[11px] font-semibold uppercase tracking-wider sayz-fms-reveal"
                style={{ color: accent, animationDelay: "60ms" }}
            >
                {copy.welcomeLabel || "Merhaba Tekrar"}
            </span>

            {/* Seni hatırlıyorum başlığı */}
            <h3
                className="text-[22px] font-bold text-foreground tracking-tight leading-tight sayz-fms-reveal"
                style={{ animationDelay: "120ms", marginTop: "0.25rem" }}
            >
                {copy.welcomeTitle || "Seni hatırlıyorum"}
            </h3>

            {/* Öneri bedeni — host DOM OOS iken opak dimle. */}
            <div
                className="font-bold tracking-tight leading-none text-foreground tabular-nums sayz-fms-reveal"
                style={{
                    fontSize: "72px",
                    letterSpacing: "-0.03em",
                    animationDelay: "180ms",
                    marginTop: "0.75rem",
                    opacity: hostStockStatus === "out-of-stock" ? 0.55 : 1,
                    transition: "opacity 220ms ease",
                }}
                data-testid="fms-welcome-recommended-size"
            >
                {recommendation.size}
            </div>

            {/* Host DOM stock cue — Result ekranıyla parity. "Seni
                hatırlıyorum" dedikten sonra "ve bu beden şu an stokta"
                diyen minimal typographic sinyal. */}
            {hostStockStatus && hostStockStatus !== "disabled" && hostStockStatus !== "not-found" && (
                <div className="sayz-fms-reveal" style={{ animationDelay: "220ms", marginTop: "0.25rem" }}>
                    <HostStockBadge status={hostStockStatus} accent={accent} />
                </div>
            )}

            {/* Info notu — beden önerisinin gerekçesi (tek satır minimal) */}
            <InfoNote testId="fms-welcome-info" animationDelay="240ms">
                Bilgilerin aynıysa bu beden sana uygun.
            </InfoNote>

            {/* Bilinenler — ortalı chip özeti */}
            <ProfileChipList chips={chips} highlightedKey={highlightedKey} centered />
        </div>
    );
};
